import {
    Box,
    Card,
    CardContent,
    Container,
    Stack,
    Typography,
    Fab,
    CircularProgress,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Add } from '@mui/icons-material';
import { AuthStatus, useAuth } from '../auth/Auth';
import InterstellarLogo from '../assets/InterstellarLogo.svg';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { Forum } from '../database/forum';
import ForumCard from './ForumCard';
import CreateForumDialog from './CreateForumDialog';
import { CreateForumRequest, ListForumsResponse } from '../api/forumApi';
import { useApi } from '../api/Api';
import { useRequest } from '../api/Request';
import VerificationCodeInput from '../components/VerificationCodeInput';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useHasForumAccess } from './useHasForumAccess';
import LockedContentOverlay from './LockedContentOverlay';
import { CommunityThemeProvider } from '../ThemeProvider';

const InterstellarPage = () => {
    const auth = useAuth();
    const api = useApi();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const createForumRequest = useRequest<Forum>();
    const listForumsRequest = useRequest<ListForumsResponse>();
    const [loading, setLoading] = useState(true);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [showPasswordInput, setShowPasswordInput] = useState(false);
    const [passwordInput, setPasswordInput] = useState('');
    const [hasAccess, setHasAccess] = useLocalStorage(
        'enterStellarSpacebarAccess',
        false,
    );
    const [data, setData] = useState<ListForumsResponse>();
    const [isPressing, setIsPressing] = useState(false);
    const lastTriggerRef = useRef<number | null>(null);
    const hasForumAccess = useHasForumAccess();

    // Check if user can create forums (artist or admin)
    const canCreateForum =
        auth.status === AuthStatus.Authenticated &&
        auth.user !== undefined &&
        (auth.user.isArtist || auth.user.isAdmin);

    // Handle keyboard events for triggering password input
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Press Enter to show password input
            if (event.key === 'Enter' && !hasAccess) {
                setShowPasswordInput((prev) => !prev);
            }
            // Press Spacebar to verify password
            if (event.key === ' ' && passwordInput === 'stellar') {
                setHasAccess(true);
                setShowPasswordInput(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [hasAccess, passwordInput, setHasAccess]);

    // Handle a single press (click or tap) that triggers a short animation,
    // then opens the password input. We guard against double-triggering on
    // touch devices using `lastTriggerRef`.
    const handleLogoPress = () => {
        if (hasAccess) return;

        const now = Date.now();
        if (lastTriggerRef.current && now - lastTriggerRef.current < 800) {
            // Already triggered recently — ignore to avoid duplicate opens on some devices
            return;
        }

        lastTriggerRef.current = now;
        setIsPressing(true);

        // Play a short animation, then open the code input
        setTimeout(() => {
            setShowPasswordInput(true);
            setIsPressing(false);
        }, 600);
    };

    // Handle password input change
    const handlePasswordChange = (value: string) => {
        setPasswordInput(value);
    };

    const handleResponse = useCallback(
        (resp: ListForumsResponse) => {
            // Create a Set of existing forum IDs for fast lookup
            const existingIds = new Set((data?.forums || []).map((f) => f.sk));

            // Filter out any forums we already have
            const newForums = resp.forums.filter((f) => !existingIds.has(f.sk));

            const allForums = (data?.forums || []).concat(newForums);

            // Sort by creation date (newest first)
            allForums.sort((lhs, rhs) => rhs.createdAt.localeCompare(lhs.createdAt));

            setData({
                forums: allForums,
                lastEvaluatedKey: resp.lastEvaluatedKey,
            });
            setLoading(false);
        },
        [data, setData],
    );

    useEffect(() => {
        if (!listForumsRequest.isSent() && hasAccess) {
            listForumsRequest.onStart();
            api.listForums()
                .then((resp) => {
                    handleResponse(resp.data);
                    listForumsRequest.onSuccess(resp.data);
                })
                .catch((err) => {
                    console.error('listForums: ', err);
                    listForumsRequest.onFailure(err);
                });
        }
    }, [listForumsRequest, api, handleResponse, hasAccess]);

    // Deep-link redirect: /ess?topic=pk~sk or /ess?topic=pk~sk&thread=pk~sk
    const deepLinkHandled = useRef(false);
    const topicParam = searchParams.get('topic');
    const threadParam = searchParams.get('thread');

    useEffect(() => {
        if (!topicParam || !data || deepLinkHandled.current) return;
        deepLinkHandled.current = true;

        const tildeIdx = topicParam.indexOf('~');
        if (tildeIdx < 0) return;
        const topicPk = topicParam.substring(0, tildeIdx); // forum sk
        const topicSk = topicParam.substring(tildeIdx + 1);

        // Find the forum name from already-loaded forums
        const forum = data.forums.find((f) => f.sk === topicPk);
        if (!forum) return;

        // Fetch topic to get its title for the URL
        api.getTopic(topicPk, topicSk)
            .then((resp) => {
                const topic = resp.data;
                const forumPath = `${encodeURIComponent(forum.name)}-${encodeURIComponent(forum.sk)}`;
                const topicPath = `${encodeURIComponent(topic.title)}-${encodeURIComponent(topic.sk)}`;

                if (threadParam) {
                    // Thread deep-link: navigate to topic page with ?thread=sk
                    const threadTildeIdx = threadParam.indexOf('~');
                    if (threadTildeIdx < 0) return;
                    const threadSk = threadParam.substring(threadTildeIdx + 1);
                    navigate(
                        `/ess/${forumPath}/topic/${topicPath}?thread=${encodeURIComponent(threadSk)}`,
                        { replace: true },
                    );
                } else {
                    // Topic deep-link: navigate to forum page with ?topic=sk
                    navigate(`/ess/${forumPath}?topic=${encodeURIComponent(topicSk)}`, {
                        replace: true,
                    });
                }
            })
            .catch((err) => {
                console.error('Failed to fetch topic for deep-link:', err);
            });
    }, [topicParam, threadParam, data, api, navigate]);

    const handleCreateForum = async (req: CreateForumRequest) => {
        createForumRequest.onStart();
        api.createForum(req)
            .then((resp) => {
                setData({
                    forums: [...(data?.forums || []), resp.data],
                    lastEvaluatedKey: data?.lastEvaluatedKey,
                });
                createForumRequest.onSuccess(resp.data);
            })
            .catch((error) => {
                console.error('Failed to create forum:', error);
                createForumRequest.onFailure(error);
            });
    };

    return (
        <CommunityThemeProvider>
            <Box
                sx={(theme) => ({
                    minHeight: '100vh',
                    bgcolor: 'background.default',
                    background: `radial-gradient(ellipse at 50% 0%, ${theme.palette.primary.main}08 0%, #060f1e 45%, #030810 100%)`,
                })}
            >
                {/* Locked: show only logo and password input */}
                {!hasAccess ? (
                    <Container maxWidth='xs' sx={{ pt: 4, pb: 12 }}>
                        <Stack spacing={3} alignItems='center' minHeight='80vh'>
                            <Stack spacing={1} alignItems='center'>
                                <motion.div
                                    animate={
                                        isPressing
                                            ? { scale: [1, 1.05, 1], opacity: [1, 0.8, 1] }
                                            : { scale: 1, opacity: 1 }
                                    }
                                    transition={
                                        isPressing
                                            ? { duration: 0.5, repeat: Infinity, ease: 'easeInOut' }
                                            : { duration: 0.2 }
                                    }
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.97 }}
                                    style={{ width: '100%', cursor: 'pointer', userSelect: 'none' }}
                                    onClick={handleLogoPress}
                                    onTouchStart={handleLogoPress}
                                >
                                    <Box
                                        display='flex'
                                        justifyContent='center'
                                        alignItems='center'
                                        sx={(theme) => ({
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main}22, ${theme.palette.secondary.main}22)`,
                                            border: `1px solid ${theme.palette.primary.main}40`,
                                            borderRadius: '30px',
                                            width: '100%',
                                            boxShadow: `0 0 40px ${theme.palette.primary.main}15`,
                                        })}
                                    >
                                        <img src={InterstellarLogo} alt='ESS logo' />
                                    </Box>
                                </motion.div>
                                <motion.div
                                    animate={{ opacity: [0.4, 1, 0.4] }}
                                    transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                                >
                                    <Typography
                                        variant='h4'
                                        sx={(theme) => ({
                                            color: theme.palette.primary.main,
                                            textShadow: `0 0 20px ${theme.palette.primary.main}80`,
                                        })}
                                    >
                                        ?
                                    </Typography>
                                </motion.div>
                            </Stack>
                            {showPasswordInput && (
                                <VerificationCodeInput
                                    length={7}
                                    onChange={handlePasswordChange}
                                    autoFocus
                                />
                            )}
                        </Stack>
                    </Container>
                ) : (
                    /* User has access — show forum list */
                    <Container maxWidth='md' sx={{ pt: 4, pb: 12 }}>
                        <Stack spacing={3}>
                            {/* Header */}
                            <motion.div
                                initial={{ opacity: 0, y: -16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, ease: 'easeOut' }}
                            >
                                <Card
                                    elevation={6}
                                    sx={(theme) => ({
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main}18 0%, #0c1b30 100%)`,
                                        border: `1px solid ${theme.palette.primary.main}38`,
                                        boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${theme.palette.primary.main}1a`,
                                    })}
                                >
                                    <CardContent sx={{ pt: 0 }}>
                                        <Stack spacing={2}>
                                            <Box
                                                display='flex'
                                                justifyContent='center'
                                                alignItems='center'
                                                sx={(theme) => ({
                                                    background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
                                                    border: `1px solid ${theme.palette.primary.main}35`,
                                                    borderRadius: '30px',
                                                    width: '100%',
                                                })}
                                            >
                                                <img src={InterstellarLogo} alt='ESS logo' />
                                            </Box>
                                            <Typography
                                                variant='h5'
                                                textAlign='center'
                                                fontWeight='bold'
                                                sx={(theme) => ({
                                                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                                    WebkitBackgroundClip: 'text',
                                                    WebkitTextFillColor: 'transparent',
                                                    backgroundClip: 'text',
                                                })}
                                            >
                                                Welcome to EnterStellar Spacebar
                                            </Typography>
                                            <Typography textAlign='center' sx={{ opacity: 0.8 }}>
                                                Your launchpad for exclusive and immersive conversations.
                                                Explore the forums below and join the discussion!
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </motion.div>

                            {/* Forums List */}
                            {loading ? (
                                <CircularProgress sx={{ alignSelf: 'center' }} />
                            ) : data?.forums.length === 0 ? (
                                <Card elevation={3}>
                                    <CardContent>
                                        <Typography textAlign='center'>
                                            No forums yet.{canCreateForum && ' Create the first one!'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ) : (
                                <LockedContentOverlay
                                    locked={!hasForumAccess}
                                    message='Subscribe to unlock!'
                                    blurAmount={4}
                                >
                                    <motion.div
                                        initial='hidden'
                                        animate='visible'
                                        variants={{
                                            hidden: {},
                                            visible: { transition: { staggerChildren: 0.07 } },
                                        }}
                                    >
                                        <Stack spacing={2}>
                                            {data?.forums.map((forum) => (
                                                <motion.div
                                                    key={forum.sk}
                                                    variants={{
                                                        hidden: { opacity: 0, y: 16 },
                                                        visible: {
                                                            opacity: 1,
                                                            y: 0,
                                                            transition: { duration: 0.32 },
                                                        },
                                                    }}
                                                >
                                                    <ForumCard forum={forum} />
                                                </motion.div>
                                            ))}
                                        </Stack>
                                    </motion.div>
                                </LockedContentOverlay>
                            )}
                        </Stack>

                        {/* Floating Action Button for creating forums */}
                        {canCreateForum && (
                            <motion.div
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.92 }}
                                style={{ position: 'fixed', bottom: 80, right: 16 }}
                            >
                                <Fab color='primary' onClick={() => setCreateDialogOpen(true)}>
                                    <Add />
                                </Fab>
                            </motion.div>
                        )}

                        {/* Create Forum Dialog */}
                        <CreateForumDialog
                            open={createDialogOpen}
                            onClose={() => setCreateDialogOpen(false)}
                            onSubmit={handleCreateForum}
                        />
                    </Container>
                )}
            </Box>
        </CommunityThemeProvider>
    );
};

export default InterstellarPage;
